import React, { useState } from 'react';

function Results({ results, onReset }) {
  const [expandedGoals, setExpandedGoals] = useState({});
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [matrixExpandedGoals, setMatrixExpandedGoals] = useState({});
  const [matrixExpandedObjectives, setMatrixExpandedObjectives] = useState({});
  const [selectedSyllabus, setSelectedSyllabus] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [matrixExpandAll, setMatrixExpandAll] = useState(false);

  // ── Detail panel expand/collapse ──────────────────────────────────────────
  const toggleGoal = (goalId) => {
    setExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
  };
  const toggleObjective = (objectiveId) => {
    setExpandedObjectives(prev => ({ ...prev, [objectiveId]: !prev[objectiveId] }));
  };
  const handleExpandCollapseAll = () => {
    if (expandAll) {
      setExpandedGoals({});
      setExpandedObjectives({});
    } else {
      const allGoals = {};
      const allObjectives = {};
      const hierarchy = results[selectedSyllabus]?.hierarchy;
      if (hierarchy) {
        Object.keys(hierarchy).forEach(goalId => {
          allGoals[goalId] = true;
          Object.keys(hierarchy[goalId].objectives || {}).forEach(objId => {
            allObjectives[objId] = true;
          });
        });
      }
      setExpandedGoals(allGoals);
      setExpandedObjectives(allObjectives);
    }
    setExpandAll(!expandAll);
  };

  // ── Matrix expand/collapse ─────────────────────────────────────────────────
  const toggleMatrixGoal = (goalId) => {
    setMatrixExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
  };
  const toggleMatrixObjective = (objId) => {
    setMatrixExpandedObjectives(prev => ({ ...prev, [objId]: !prev[objId] }));
  };
  const handleMatrixExpandCollapseAll = () => {
    if (matrixExpandAll) {
      setMatrixExpandedGoals({});
      setMatrixExpandedObjectives({});
    } else {
      const allGoals = {};
      const allObjectives = {};
      results.forEach(result => {
        Object.entries(result.hierarchy || {}).forEach(([goalId, goalData]) => {
          allGoals[goalId] = true;
          Object.keys(goalData.objectives || {}).forEach(objId => {
            allObjectives[objId] = true;
          });
        });
      });
      setMatrixExpandedGoals(allGoals);
      setMatrixExpandedObjectives(allObjectives);
    }
    setMatrixExpandAll(!matrixExpandAll);
  };

  // ── Excel export ───────────────────────────────────────────────────────────
  const handleExportToExcel = async () => {
    setExporting(true);
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    try {
      const response = await fetch(`${API_BASE_URL}/export-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ results }),
      });
      if (!response.ok) throw new Error('Failed to export to Excel');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AOL_Analysis_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export to Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getAlignmentColor = (alignment) => {
    if (alignment === 'heavy')    return 'bg-green-500';
    if (alignment === 'moderate') return 'bg-yellow-500';
    return 'bg-gray-300';
  };
  const getAlignmentLabel = (alignment) => {
    if (alignment === 'heavy')    return 'H';
    if (alignment === 'moderate') return 'M';
    return '';
  };
  const getAlignmentBg = (alignment) => {
    if (alignment === 'heavy')    return 'bg-green-50';
    if (alignment === 'moderate') return 'bg-yellow-50';
    return '';
  };

  // Build merged hierarchy from all results (union of all goals)
  const getMergedHierarchy = () => {
    const merged = {};
    results.forEach(result => {
      Object.entries(result.hierarchy || {}).forEach(([goalId, goalData]) => {
        if (!merged[goalId]) merged[goalId] = goalData;
      });
    });
    return Object.entries(merged).sort((a, b) => {
      const an = parseFloat(a[0]);
      const bn = parseFloat(b[0]);
      return isNaN(an) || isNaN(bn) ? a[0].localeCompare(b[0]) : an - bn;
    });
  };

  const getResultAlignment = (result, goalId, objId = null, outcomeId = null) => {
    const h = result.hierarchy || {};
    if (!h[goalId]) return 'none';
    if (!objId) return h[goalId].alignment || 'none';
    if (!h[goalId].objectives?.[objId]) return 'none';
    if (!outcomeId) return h[goalId].objectives[objId].alignment || 'none';
    return h[goalId].objectives[objId].outcomes?.[outcomeId]?.alignment || 'none';
  };

  const currentResult = results[selectedSyllabus];
  const analysis = currentResult.analysis;
  const hierarchy = currentResult.hierarchy;
  const costInfo = analysis.cost_info || {};
  const mergedHierarchy = getMergedHierarchy();

  // Aggregate cost across all results
  const totalTokens = results.reduce((sum, r) => sum + (r.analysis.cost_info?.total_tokens || 0), 0);
  const totalCost = results.reduce((sum, r) => sum + (r.analysis.cost_info?.total_cost_usd || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Header ── */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Analysis Results</h1>
              <p className="text-gray-600 mt-1">
                Analyzed {results.length} syllabus{results.length !== 1 ? 'es' : ''}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleExportToExcel}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export to Excel
                  </>
                )}
              </button>
              <button
                onClick={onReset}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Analyze More
              </button>
            </div>
          </div>

          {/* Token/Cost Stats */}
          {totalTokens > 0 && (
            <div className="grid grid-cols-3 gap-4 mt-4 p-4 bg-gray-50 rounded-lg text-sm">
              <div className="text-center">
                <div className="font-semibold text-gray-700">Total Tokens Used</div>
                <div className="text-lg font-bold text-blue-600">{totalTokens.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-700">Estimated Cost</div>
                <div className="text-lg font-bold text-green-600">
                  {totalCost === 0 ? 'Free' : `$${totalCost.toFixed(4)}`}
                </div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-700">Model</div>
                <div className="text-lg font-bold text-purple-600">{costInfo.model_id || 'N/A'}</div>
              </div>
            </div>
          )}

          {/* Syllabus Selector */}
          {results.length > 1 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Syllabus for Detailed View:
              </label>
              <select
                value={selectedSyllabus}
                onChange={(e) => {
                  setSelectedSyllabus(parseInt(e.target.value));
                  setExpandedGoals({});
                  setExpandedObjectives({});
                  setExpandAll(false);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {results.map((result, index) => (
                  <option key={index} value={index}>{result.filename}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ── Course-Goal Alignment Matrix (expandable) ── */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Course-Goal Alignment Matrix</h2>
            <button
              onClick={handleMatrixExpandCollapseAll}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
            >
              {matrixExpandAll ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border border-gray-300 bg-blue-700 text-white px-4 py-2 text-left font-semibold min-w-64">
                    AOL Goal / Objective / Outcome
                  </th>
                  {results.map((result, idx) => (
                    <th key={idx} className="border border-gray-300 bg-blue-700 text-white px-3 py-2 text-center font-semibold min-w-24">
                      {result.analysis.course_info?.course_code || `Course ${idx + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mergedHierarchy.map(([goalId, goalData]) => {
                  const isGoalExpanded = matrixExpandedGoals[goalId];
                  return (
                    <React.Fragment key={goalId}>
                      {/* Level 1 row */}
                      <tr className="bg-blue-50 hover:bg-blue-100 cursor-pointer" onClick={() => toggleMatrixGoal(goalId)}>
                        <td className="border border-gray-300 px-3 py-2 font-bold text-sm">
                          <span className="mr-2 text-blue-600">
                            {isGoalExpanded ? '▼' : '▶'}
                          </span>
                          Goal {goalId}: {goalData.text}
                        </td>
                        {results.map((result, idx) => {
                          const status = getResultAlignment(result, goalId);
                          const label = getAlignmentLabel(status);
                          return (
                            <td key={idx} className="border border-gray-300 px-3 py-2 text-center">
                              {label && (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${getAlignmentColor(status)} text-white font-bold text-xs`}>
                                  {label}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Level 2 rows */}
                      {isGoalExpanded && Object.entries(goalData.objectives || {}).map(([objId, objData]) => {
                        const isObjExpanded = matrixExpandedObjectives[objId];
                        return (
                          <React.Fragment key={objId}>
                            <tr className="bg-gray-50 hover:bg-gray-100 cursor-pointer" onClick={() => toggleMatrixObjective(objId)}>
                              <td className="border border-gray-300 px-3 py-2 text-sm pl-10">
                                <span className="mr-2 text-gray-500">
                                  {isObjExpanded ? '▼' : '▶'}
                                </span>
                                <span className="font-semibold">Goal {objId}:</span> {objData.text}
                              </td>
                              {results.map((result, idx) => {
                                const status = getResultAlignment(result, goalId, objId);
                                const label = getAlignmentLabel(status);
                                return (
                                  <td key={idx} className="border border-gray-300 px-3 py-2 text-center">
                                    {label && (
                                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${getAlignmentColor(status)} text-white font-bold text-xs`}>
                                        {label}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Level 3 rows */}
                            {isObjExpanded && Object.entries(objData.outcomes || {}).map(([outcomeId, outcomeData]) => (
                              <tr key={outcomeId} className="bg-white">
                                <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 pl-20">
                                  <span className="font-medium">Goal {outcomeId}:</span> {outcomeData.text}
                                </td>
                                {results.map((result, idx) => {
                                  const status = getResultAlignment(result, goalId, objId, outcomeId);
                                  const label = getAlignmentLabel(status);
                                  return (
                                    <td key={idx} className="border border-gray-300 px-3 py-2 text-center">
                                      {label && (
                                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${getAlignmentColor(status)} text-white font-bold text-xs`}>
                                          {label}
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block w-6 h-6 rounded-full bg-green-500"></span>
              <span>H = Heavily Aligned</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-6 h-6 rounded-full bg-yellow-500"></span>
              <span>M = Moderately Aligned</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-6 h-6 rounded-full bg-gray-300"></span>
              <span>(blank) = Not Aligned</span>
            </div>
          </div>
        </div>

        {/* ── Detailed Analysis for Selected Syllabus ── */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Detailed Analysis: {currentResult.filename}
            </h2>
            <button
              onClick={handleExpandCollapseAll}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
            >
              {expandAll ? 'Collapse All' : 'Expand All'}
            </button>
          </div>

          {/* Course Information */}
          {analysis.course_info && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Course Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium">Course Code:</span> {analysis.course_info.course_code || 'N/A'}</div>
                <div><span className="font-medium">Section:</span> {analysis.course_info.section || 'N/A'}</div>
                <div><span className="font-medium">Instructor:</span> {analysis.course_info.instructor || 'N/A'}</div>
                <div><span className="font-medium">Semester:</span> {analysis.course_info.semester || 'N/A'}</div>
              </div>
            </div>
          )}

          {/* Per-syllabus token stats */}
          {costInfo.total_tokens > 0 && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 flex gap-6">
              <span>Input tokens: <strong>{costInfo.input_tokens?.toLocaleString()}</strong></span>
              <span>Output tokens: <strong>{costInfo.output_tokens?.toLocaleString()}</strong></span>
              <span>Total tokens: <strong>{costInfo.total_tokens?.toLocaleString()}</strong></span>
              <span>Cost: <strong>{costInfo.total_cost_usd === 0 ? 'Free' : `$${costInfo.total_cost_usd?.toFixed(6)}`}</strong></span>
            </div>
          )}

          {/* Legend */}
          <div className="mb-4 flex gap-4 text-sm p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="inline-block w-5 h-5 rounded-full bg-green-500"></span>
              <span>H = Heavily Aligned</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-5 h-5 rounded-full bg-yellow-500"></span>
              <span>M = Moderately Aligned</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-5 h-5 rounded-full bg-gray-300"></span>
              <span>(blank) = Not Aligned</span>
            </div>
          </div>

          {/* Hierarchical Goal Display */}
          <div className="space-y-2">
            {hierarchy && Object.entries(hierarchy).map(([goalId, goalData]) => {
              const isGoalExpanded = expandedGoals[goalId];
              const hasObjectives = Object.keys(goalData.objectives || {}).length > 0;

              return (
                <div key={goalId} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Level 1: Goal */}
                  <div
                    className={`flex items-center p-4 cursor-pointer hover:bg-gray-50 ${getAlignmentBg(goalData.alignment) || 'bg-gray-50'}`}
                    onClick={() => hasObjectives && toggleGoal(goalId)}
                  >
                    {hasObjectives ? (
                      <svg className={`w-5 h-5 mr-2 transition-transform flex-shrink-0 ${isGoalExpanded ? 'transform rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    ) : <div className="w-5 mr-2 flex-shrink-0"></div>}
                    <div className="flex-1">
                      <span className="font-bold text-base">Goal {goalId}:</span>{' '}
                      <span className="text-gray-800">{goalData.text}</span>
                    </div>
                    <div className={`w-8 h-8 rounded-full ${getAlignmentColor(goalData.alignment)} flex items-center justify-center text-white font-bold ml-4 flex-shrink-0`}>
                      {getAlignmentLabel(goalData.alignment)}
                    </div>
                  </div>

                  {/* Level 2: Objectives */}
                  {isGoalExpanded && goalData.objectives && (
                    <div className="bg-white">
                      {Object.entries(goalData.objectives).map(([objId, objData]) => {
                        const isObjExpanded = expandedObjectives[objId];
                        const hasOutcomes = Object.keys(objData.outcomes || {}).length > 0;

                        return (
                          <div key={objId} className="border-t border-gray-200">
                            <div
                              className={`flex items-center p-3 pl-12 cursor-pointer hover:bg-gray-50 ${getAlignmentBg(objData.alignment)}`}
                              onClick={() => hasOutcomes && toggleObjective(objId)}
                            >
                              {hasOutcomes ? (
                                <svg className={`w-4 h-4 mr-2 transition-transform flex-shrink-0 ${isObjExpanded ? 'transform rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              ) : <div className="w-4 mr-2 flex-shrink-0"></div>}
                              <div className="flex-1 text-sm">
                                <span className="font-semibold">Goal {objId}:</span>{' '}
                                <span className="text-gray-700">{objData.text}</span>
                              </div>
                              <div className={`w-7 h-7 rounded-full ${getAlignmentColor(objData.alignment)} flex items-center justify-center text-white font-bold ml-4 flex-shrink-0 text-sm`}>
                                {getAlignmentLabel(objData.alignment)}
                              </div>
                            </div>

                            {/* Level 3: Outcomes */}
                            {isObjExpanded && objData.outcomes && (
                              <div className="bg-gray-50">
                                {Object.entries(objData.outcomes).map(([outcomeId, outcomeData]) => (
                                  <div key={outcomeId} className="border-t border-gray-200 p-3 pl-20">
                                    <div className="flex items-start">
                                      <div className="flex-1">
                                        <div className="text-sm">
                                          <span className="font-medium">Goal {outcomeId}:</span>{' '}
                                          <span className="text-gray-700">{outcomeData.text}</span>
                                        </div>
                                        {/* Evidence */}
                                        {outcomeData.evidence && outcomeData.evidence.length > 0 && (
                                          <div className="mt-2 space-y-1">
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Evidence from Syllabus:</div>
                                            {outcomeData.evidence.map((ev, i) => (
                                              <div key={i} className="text-xs text-gray-600 bg-white border-l-2 border-blue-300 pl-2 py-1 italic">
                                                "{ev}"
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <div className={`w-6 h-6 rounded-full ${getAlignmentColor(outcomeData.alignment)} flex items-center justify-center text-white text-xs font-bold ml-4 flex-shrink-0 mt-0.5`}>
                                        {getAlignmentLabel(outcomeData.alignment)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary */}
          {analysis.summary && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Summary</h3>
              <p className="text-gray-700 text-sm">{analysis.summary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Results;
